/**
 * Stock Audit helper — physical inventory count.
 *
 * Lifecycle:  Counting → PendingApproval → Approved   (or Rejected / Cancelled)
 *
 * FREEZE: while an audit is `Counting`, its location is locked and every stock-OUT
 * movement there is blocked (POS, sales, transfers out). The lock lives in ONE tiny
 * doc — companies/{id}/settings/stockAuditLocks — and is read through a short
 * in-memory cache, so companies with no audit running pay ~nothing and behave
 * exactly as before.
 *
 * BLIND COUNT: the counter never sees book qty. `bookQty` is snapshotted per item
 * the first time it is touched — safe, because stock cannot move out of a locked
 * location while counting. INV-10: stock can still come IN while counting (a
 * purchase, a transfer in), so on submit every counted line's book qty is taken
 * again as of the moment that line was last counted — goods received before an
 * item was counted no longer show as excess, and goods received after it was
 * counted no longer show as a shortage. The opening snapshot is kept as
 * `bookQtyAtStart` so the review can show what moved during the count.
 *
 * FULL vs PARTIAL count (INV-10): a PARTIAL count (the default for new counts)
 * adjusts only the items that were counted. A FULL count also treats every item
 * with stock here that was NOT counted as counted = 0 (a shortage). Counts started
 * before this choice existed have no `countMode` and keep working as FULL counts.
 *
 * NOTE: this module must NOT import transactionHelper (that would be circular —
 * transactionHelper imports the freeze guard from here). The page posts the SAJ
 * adjustment itself using buildAdjustmentLines().
 */

import { db } from '../config/firebase';
import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, addDoc, query, where, orderBy, onSnapshot,
} from 'firebase/firestore';
import { getTranType } from '../config/tranTypes';
import { activeFyId, inActiveFy } from './stockFlowHelper';

const LOCKS_PATH = (companyId) => doc(db, 'companies', companyId, 'settings', 'stockAuditLocks');
const AUDITS = (companyId) => collection(db, 'companies', companyId, 'stockAudits');
const LINES = (companyId, auditId) => collection(db, 'companies', companyId, 'stockAudits', auditId, 'lines');

// ─────────────────────────────────────────────────────────────
// LOCKS (the freeze)
// ─────────────────────────────────────────────────────────────
const LOCK_TTL_MS = 10000; // 10s — short enough that a new freeze takes effect fast
let _cache = { companyId: null, locks: null, at: 0 };

export function invalidateLockCache() {
  _cache = { companyId: null, locks: null, at: 0 };
}

/** Returns { [locationId]: { auditId, startedAt, startedBy } }. Cached. */
// Speed (measured ~0.6 s on every save): instead of re-reading the lock doc
// every 10 s, keep ONE live listener on it. Every save then checks instantly,
// and a new freeze still takes effect at once (the listener pushes it).
// Cost: 1 read when it starts + 1 per change (changes are rare).
let _live = { companyId: null, locks: null, unsub: null };
function ensureLockListener(companyId) {
  if (_live.companyId === companyId && _live.unsub) return;
  try { if (_live.unsub) _live.unsub(); } catch (_) { /* ignore */ }
  _live = { companyId, locks: null, unsub: null };
  try {
    _live.unsub = onSnapshot(LOCKS_PATH(companyId), { includeMetadataChanges: true }, (snap) => {
      if (_live.companyId !== companyId) return;
      // Only trust what the SERVER confirmed — never an old copy from this
      // browser's offline store. Until then, callers use the direct read below.
      if (snap.metadata && snap.metadata.fromCache) return;
      _live.locks = snap.exists() ? (snap.data().locks || {}) : {};
    }, () => { /* listener error → fall back to direct reads */ _live.unsub = null; _live.locks = null; });
  } catch (_) { _live.unsub = null; }
}

export async function getLocationLocks(companyId) {
  if (!companyId) return {};
  ensureLockListener(companyId);
  if (_live.companyId === companyId && _live.locks) return _live.locks;
  const now = Date.now();
  if (_cache.companyId === companyId && _cache.locks && now - _cache.at < LOCK_TTL_MS) {
    return _cache.locks;
  }
  try {
    const snap = await getDoc(LOCKS_PATH(companyId));
    const locks = snap.exists() ? (snap.data().locks || {}) : {};
    _cache = { companyId, locks, at: now };
    return locks;
  } catch (e) {
    // Never let a lock-read failure block business. Fall back to last known / none.
    return (_cache.companyId === companyId && _cache.locks) || {};
  }
}

export async function isLocationLocked(companyId, locationId) {
  if (!companyId || !locationId) return false;
  const locks = await getLocationLocks(companyId);
  return !!locks[locationId];
}

async function setLock(companyId, locationId, value) {
  const snap = await getDoc(LOCKS_PATH(companyId));
  const locks = snap.exists() ? (snap.data().locks || {}) : {};
  if (value) locks[locationId] = value; else delete locks[locationId];
  await setDoc(LOCKS_PATH(companyId), { locks, updatedAt: new Date().toISOString() }, { merge: true });
  invalidateLockCache();
}

const FROZEN_MSG = (name) =>
  `Stock count in progress at ${name || 'this location'} — sales and stock-out are paused until the count is submitted.`;

/** Resolve the location a transaction would take stock OUT of (mirrors stockFlowHelper). */
async function resolveOutLocations(companyId, data) {
  const ids = new Set();
  (data.lines || []).forEach((l) => {
    if (l.locationId) ids.add(l.locationId);
    if (l.fromLocationId) ids.add(l.fromLocationId); // Stock Transfer: the OUT side
  });
  if (data.locationId) ids.add(data.locationId);

  if (ids.size === 0) {
    // No explicit location (e.g. POS) → branch's location, else the default location.
    let locId = null;
    if (data.branchId && data.branchId !== 'all') {
      try {
        const b = await getDoc(doc(db, 'companies', companyId, 'branches', data.branchId));
        if (b.exists()) locId = b.data().defaultLocationId || null;
      } catch (_) { /* ignore */ }
    }
    if (!locId) {
      try {
        const q = query(collection(db, 'companies', companyId, 'inventoryLocations'), where('isDefault', '==', true));
        const s = await getDocs(q);
        if (!s.empty) locId = s.docs[0].id;
      } catch (_) { /* ignore */ }
    }
    if (locId) ids.add(locId);
  }
  return [...ids];
}

/**
 * Guard used by transactionHelper for every NON-POS document.
 * Fast path: if nothing is locked (the case for every company today), this does a
 * single cached read of one tiny doc and returns — no behaviour change.
 */
export async function assertLocationsUnlocked(companyId, tranTypeCode, data) {
  const locks = await getLocationLocks(companyId);
  if (!locks || Object.keys(locks).length === 0) return; // nothing frozen anywhere

  if (data?.status === 'Draft') return; // a Draft doesn't move stock — don't block it

  const tt = getTranType(tranTypeCode);
  const dir = tt?.stockDirection;
  if (!dir || dir === 'in') return; // only stock-OUT is blocked; receiving is fine

  const locIds = await resolveOutLocations(companyId, data);
  for (const id of locIds) {
    if (locks[id]) {
      const err = new Error(FROZEN_MSG(locks[id].locationName));
      err.code = 'LOCATION_AUDIT_LOCKED';
      throw err;
    }
  }
}

/** Guard used by the POS save path (covers online + offline). */
export async function assertPosLocationUnlocked(companyId, locationId) {
  if (!companyId || !locationId) return;
  const locks = await getLocationLocks(companyId);
  if (locks[locationId]) {
    const err = new Error(FROZEN_MSG(locks[locationId].locationName));
    err.code = 'LOCATION_AUDIT_LOCKED';
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────
// STOCK SNAPSHOT (per location)
// ─────────────────────────────────────────────────────────────
// 'Invoiced' included so a DC/GRN that posted stock (DC-out / GRN-in modes) keeps
// counting in the audit book-quantity after it's converted and flipped to 'Invoiced'.
// INV-01 fix (2026-09-30): 'Sent' = Send Inventory OUT leg (source branch). It was
// missing, so sending never reduced the source branch and stock was counted twice.
const ACTIVE_STOCK_STATUSES = ['Posted', 'Received', 'Dispatched', 'Delivered', 'Invoiced', 'Completed', 'InProgress', 'Partial', 'Paid', 'Sent', 'Partially Received'];

/**
 * One read of a location's stock rows. Returns the on-hand qty per item and,
 * when `withTimes`, each item's movements with the time they were recorded
 * (so the qty can be worked out "as of" an earlier moment — INV-10).
 */
async function readLocationStock(companyId, locationId, withTimes) {
  const out = {};
  const moves = {};
  if (!companyId || !locationId) return { out, moves };
  const q = query(collection(db, 'companies', companyId, 'stockFlow'), where('locationId', '==', locationId));
  const snap = await getDocs(q);
  // Active financial year only — otherwise last year's opening stock is counted on
  // top of this year's and the audit's "book quantity" would be double.
  const fy = activeFyId();
  snap.docs.forEach((d) => {
    const t = d.data();
    if (!t.itemId) return;
    if (!inActiveFy(t, fy)) return;
    if (!ACTIVE_STOCK_STATUSES.includes(t.status)) return;
    const qty = Number(t.quantity) || 0;
    if (out[t.itemId] === undefined) out[t.itemId] = 0;
    let signed = 0;
    if (t.direction === 'in') signed = qty;
    else if (t.direction === 'out') signed = -qty;
    out[t.itemId] += signed;
    if (withTimes && signed !== 0) {
      (moves[t.itemId] || (moves[t.itemId] = [])).push({ at: typeof t.createdAt === 'string' ? t.createdAt : '', q: signed });
    }
  });
  return { out, moves };
}

/** { [itemId]: qty } currently on hand at ONE location. */
export async function getLocationStock(companyId, locationId) {
  return (await readLocationStock(companyId, locationId, false)).out;
}

// ─────────────────────────────────────────────────────────────
// AUDIT LIFECYCLE
// ─────────────────────────────────────────────────────────────
export async function loadAudits(companyId) {
  const snap = await getDocs(query(AUDITS(companyId), orderBy('startedAt', 'desc')));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function loadAuditLines(companyId, auditId) {
  const snap = await getDocs(LINES(companyId, auditId));
  // `excluded` = an item removed from a PARTIAL count before submit — it was not
  // counted, is never adjusted, and is not shown anywhere.
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((l) => l.excluded !== true);
}

/** One audit, fresh from the server (approval re-checks what it already posted). */
export async function loadAudit(companyId, auditId) {
  const snap = await getDoc(doc(AUDITS(companyId), auditId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** The audit document (approval records each posted voucher on it — INV-10). */
export const auditDocRef = (companyId, auditId) => doc(AUDITS(companyId), auditId);

/**
 * INV-10: FULL count = uncounted items are counted as 0. Counts started before
 * the Full / Partial choice existed have no `countMode` and stay FULL (what their
 * counter was told when the count started).
 */
export const isFullCountAudit = (audit) => (audit && audit.countMode ? audit.countMode === 'full' : true);

/** Start a count: creates the audit and FREEZES the location. */
export async function startAudit(companyId, payload, user) {
  const { branchId, branchName, locationId, locationName, isFullCount, countMode } = payload;
  if (!locationId) throw new Error('Pick a location to count.');
  // INV-10: 'partial' (only counted items are adjusted) unless FULL is chosen.
  const mode = (countMode === 'full' || countMode === 'partial') ? countMode : (isFullCount ? 'full' : 'partial');

  // One open audit per location.
  const openSnap = await getDocs(query(AUDITS(companyId), where('status', '==', 'Counting')));
  const clash = openSnap.docs.find((d) => d.data().locationId === locationId);
  if (clash) throw new Error(`A count is already open at ${locationName || 'this location'}.`);

  const now = new Date().toISOString();
  const ref = await addDoc(AUDITS(companyId), {
    branchId: branchId || null,
    branchName: branchName || null,
    locationId,
    locationName: locationName || '',
    status: 'Counting',
    countMode: mode,
    isFullCount: mode === 'full',
    startedAt: now,
    startedBy: user?.uid || null,
    startedByName: user?.name || '',
    totals: null,
    postedTransactionId: null,
  });
  await setLock(companyId, locationId, { auditId: ref.id, locationName: locationName || '', startedAt: now, startedBy: user?.uid || null });
  return ref.id;
}

/**
 * Snapshot the book qty + cost for an item the first time it's counted.
 * Safe to call at first-touch (not at start) because stock can't leave a locked location.
 */
export async function prepareLine(companyId, item, locationId) {
  const stock = await getLocationStock(companyId, locationId);
  return {
    bookQty: Number(stock[item.id] || 0),
    unitCost: Number(item.averageCost) || Number(item.purchasePrice) || 0,
  };
}

/** Upsert one counted line (page keeps the running qty and calls this). */
export async function saveAuditLine(companyId, auditId, line, user) {
  await setDoc(doc(LINES(companyId, auditId), line.itemId), {
    itemId: line.itemId,
    itemCode: line.itemCode || '',
    itemName: line.itemName || '',
    barcode: line.barcode || '',
    unitOfMeasure: line.unitOfMeasure || '',
    countedQty: Number(line.countedQty) || 0,
    bookQty: Number(line.bookQty) || 0,
    unitCost: Number(line.unitCost) || 0,
    // INV-10: taken off the count (bin icon) — in a PARTIAL count it is left untouched.
    removed: !!line.removed,
    countedBy: user?.uid || null,
    countedAt: new Date().toISOString(),
  }, { merge: true });
}

/**
 * Submit: lock in the variances, roll up totals, and UNFREEZE the location so the
 * store can trade again while an admin reviews.
 */
export async function submitAudit(companyId, audit, user, itemsById = {}) {
  const full = isFullCountAudit(audit);
  const allLines = await loadAuditLines(companyId, audit.id);
  // INV-10 PARTIAL count: an item taken off the count (bin icon) was not counted —
  // it is left untouched. In a FULL count it stays counted = 0, as before.
  const lines = full ? allLines : allLines.filter((l) => !l.removed);
  const dropped = full ? [] : allLines.filter((l) => l.removed);
  const counted = new Set(allLines.map((l) => l.itemId));

  // INV-10: the book qty of every counted line is the stock at the moment THAT
  // line was last counted (its countedAt): stock received before the item was
  // counted is in the book qty (no false excess), and stock received after it
  // was counted is not (no false shortage — those goods were not on the shelf
  // when it was counted). Lines with no count time use the stock at the end of
  // the count. Same single read of the location as before — no extra reads.
  // The opening snapshot is kept as bookQtyAtStart (a retried submit keeps the
  // first one).
  const { out: stock, moves } = await readLocationStock(companyId, audit.locationId, true);
  const bookAsOf = (itemId, when) => {
    let q = Number(stock[itemId] || 0);
    if (!when) return q;
    (moves[itemId] || []).forEach((m) => { if (m.at && m.at > when) q -= m.q; });
    return Math.round(q * 1e6) / 1e6;
  };
  const atCountEnd = (l) => ({
    ...l,
    bookQtyAtStart: l.bookQtyAtStart ?? (Number(l.bookQty) || 0),
    bookQty: bookAsOf(l.itemId, typeof l.countedAt === 'string' ? l.countedAt : ''),
  });

  // FULL count only: anything that HAS stock at this location but was never
  // counted is a SHORTAGE (counted = 0). Valued properly from the item's cost so
  // the shortage isn't Rs.0. The admin sees these in the review and can edit or
  // zero them out before approving.
  const extra = [];
  if (full) {
    for (const [itemId, qty] of Object.entries(stock)) {
      if (counted.has(itemId) || qty <= 0.0001) continue;
      const it = itemsById[itemId] || {};
      extra.push({
        itemId,
        itemCode: it.itemCode || '',
        itemName: it.itemName || '(unknown item)',
        barcode: it.barcode || '',
        unitOfMeasure: it.unitOfMeasure || 'Pcs',
        countedQty: 0,
        bookQty: qty,
        unitCost: Number(it.averageCost) || Number(it.purchasePrice) || 0,
        notCounted: true,
      });
    }
  }

  const all = [...lines.map(atCountEnd), ...extra];
  let itemsWithVariance = 0, shortageValue = 0, excessValue = 0;
  for (const l of all) {
    const variance = (Number(l.countedQty) || 0) - (Number(l.bookQty) || 0);
    const value = Math.abs(variance) * (Number(l.unitCost) || 0);
    if (Math.abs(variance) > 0.0001) {
      itemsWithVariance++;
      if (variance < 0) shortageValue += value; else excessValue += value;
    }
    await setDoc(doc(LINES(companyId, audit.id), l.itemId), { ...l, variance }, { merge: true });
  }
  // PARTIAL count: the items taken off the count are never adjusted.
  for (const l of dropped) {
    await setDoc(doc(LINES(companyId, audit.id), l.itemId), { excluded: true, variance: 0 }, { merge: true });
  }

  const r2 = (n) => Math.round(n * 100) / 100;
  await updateDoc(doc(AUDITS(companyId), audit.id), {
    status: 'PendingApproval',
    submittedAt: new Date().toISOString(),
    submittedBy: user?.uid || null,
    totals: {
      itemsCounted: lines.length,
      itemsWithVariance,
      shortageValue: r2(shortageValue),
      excessValue: r2(excessValue),
    },
  });

  // Sales resume immediately — the variance is already locked in.
  await setLock(companyId, audit.locationId, null);
}

/** Admin edits a counted qty in the review — re-saves the line with the new variance. */
export async function updateLineCount(companyId, auditId, line, newCountedQty) {
  const countedQty = Number(newCountedQty) || 0;
  const variance = countedQty - (Number(line.bookQty) || 0);
  await setDoc(doc(LINES(companyId, auditId), line.itemId), { countedQty, variance }, { merge: true });
  return { ...line, countedQty, variance };
}

/**
 * Pure: split the audit's differences into lines for the TWO existing vouchers.
 *   excess   (counted > book) → an ADD STOCK (AS) voucher
 *   shortage (counted < book) → a REDUCE STOCK (RS) voucher
 * No new document type — these are the vouchers AmalERP already has.
 */
export function buildAdjustmentSplit(auditLines, locationId, locationName) {
  const excess = [];
  const shortage = [];
  (auditLines || []).forEach((l) => {
    if (l.excluded) return; // INV-10: not counted in a partial count — never adjusted
    const variance = Number(l.variance ?? ((Number(l.countedQty) || 0) - (Number(l.bookQty) || 0)));
    if (Math.abs(variance) < 0.0001) return;
    const qty = Math.abs(variance);
    const unitCost = Number(l.unitCost) || 0;
    const line = {
      itemId: l.itemId,
      itemCode: l.itemCode || '',
      itemName: l.itemName || '',
      description: l.itemName || '',
      quantity: qty,
      unitCost,
      unitPrice: unitCost,
      totalCost: Math.round(qty * unitCost * 100) / 100,
      unitOfMeasure: l.unitOfMeasure || 'Pcs',
      unitCode: l.unitOfMeasure || '',
      locationId,
      locationName: locationName || '',
    };
    if (variance > 0) excess.push(line); else shortage.push(line);
  });
  return { excess, shortage };
}

/**
 * INV-10: record a voucher the approval just posted, so a retried approval never
 * posts it twice. (Normally this is written in the SAME save as the voucher —
 * see StockAudit handleApprove; this direct write is the fallback.)
 */
export async function recordAuditVouchers(companyId, auditId, fields) {
  await updateDoc(doc(AUDITS(companyId), auditId), fields);
}

export async function markApproved(companyId, auditId, posted, user) {
  await updateDoc(doc(AUDITS(companyId), auditId), {
    status: 'Approved',
    approvedAt: new Date().toISOString(),
    approvedBy: user?.uid || null,
    // The Add Stock / Reduce Stock vouchers this audit posted — id (to open the
    // document) and docNumber (to show the admin what was posted).
    postedAddStockId: posted?.addStockId || null,
    postedAddStockNumber: posted?.addStockNumber || null,
    postedReduceStockId: posted?.reduceStockId || null,
    postedReduceStockNumber: posted?.reduceStockNumber || null,
  });
}

/** Send back for a recount (stock untouched). */
export async function rejectAudit(companyId, audit, user) {
  await updateDoc(doc(AUDITS(companyId), audit.id), {
    status: 'Rejected',
    rejectedAt: new Date().toISOString(),
    rejectedBy: user?.uid || null,
  });
}

/** Abandon a count — ALWAYS unfreezes the location (the escape hatch). */
export async function cancelAudit(companyId, audit, user) {
  await updateDoc(doc(AUDITS(companyId), audit.id), {
    status: 'Cancelled',
    cancelledAt: new Date().toISOString(),
    cancelledBy: user?.uid || null,
  });
  await setLock(companyId, audit.locationId, null);
}

export default {
  getLocationLocks, isLocationLocked, invalidateLockCache,
  assertLocationsUnlocked, assertPosLocationUnlocked,
  getLocationStock, loadAudits, loadAuditLines,
  startAudit, prepareLine, saveAuditLine, submitAudit, updateLineCount,
  buildAdjustmentSplit, markApproved, rejectAudit, cancelAudit,
  loadAudit, auditDocRef, isFullCountAudit, recordAuditVouchers,
};
